import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip, LinearProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import { COLORS, getStatusTone } from "../UserDashboard/dashboardConfig";

// Wspólna "powłoka" list kontrolnych (Eksport / Import / Tranzyt) w stylu DetailPage:
// nagłówek z nr ref i statusem, karty ról, postęp wypełnienia, miejsce na akcje.
// Sama nie zawiera żadnej logiki biznesowej - dostaje gotowe dane i dzieci.

const useStyles = makeStyles((theme) => ({
  page: { minHeight: "100vh", background: "#fff" },
  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 3),
    minHeight: 60,
    background: "#fff",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1.5),
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "#424242",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": { background: "#f5f5f5", color: COLORS.primaryDark },
  },
  divider: {
    width: 1,
    alignSelf: "stretch",
    margin: theme.spacing(0.5, 0),
    background: COLORS.border,
  },
  titleWrap: {
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: theme.spacing(1.25),
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 800,
    color: COLORS.primaryDark,
  },
  nrRef: { fontSize: 20, fontWeight: 800, color: COLORS.primary },
  kind: {
    padding: "2px 10px",
    borderRadius: 12,
    background: COLORS.primarySoft,
    color: COLORS.primaryDark,
    fontSize: 13,
    fontWeight: 700,
  },
  pill: {
    display: "inline-block",
    padding: "3px 12px",
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  content: {
    maxWidth: 1600,
    margin: "0 auto",
    padding: theme.spacing(3),
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  card: {
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  roleLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#757575",
  },
  roleValue: { marginTop: 4, fontSize: 16, fontWeight: 700, color: "#212121" },
  roleEmpty: { color: "#bdbdbd", fontWeight: 500 },
  roleYou: {
    marginLeft: 8,
    padding: "1px 8px",
    borderRadius: 10,
    background: COLORS.success,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    verticalAlign: "middle",
  },
  progressCard: {
    gridColumn: "span 2",
    "@media (max-width: 700px)": { gridColumn: "auto" },
  },
  progressHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: theme.spacing(1),
  },
  progressValue: {
    fontSize: 16,
    fontWeight: 800,
    color: "#212121",
    fontVariantNumeric: "tabular-nums",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  section: {
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    padding: theme.spacing(1.25, 2),
    fontSize: 17,
    fontWeight: 800,
    color: COLORS.primary,
    borderBottom: `2px solid ${COLORS.primary}`,
  },
  sectionBody: { padding: theme.spacing(2) },
  sectionActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
}));

export const StatusPill = ({ status }) => {
  const classes = useStyles();
  if (!status) return null;
  const tone = getStatusTone(status);
  return (
    <span
      className={classes.pill}
      style={{ background: tone.bg, color: tone.fg }}
    >
      {status}
    </span>
  );
};

// Karta sekcji (np. "Weryfikacja") w stylu kart DetailPage.
export const SectionCard = ({ title, children }) => {
  const classes = useStyles();
  return (
    <section className={classes.section}>
      <div className={classes.sectionTitle}>{title}</div>
      <div className={classes.sectionBody}>{children}</div>
    </section>
  );
};

export const SectionActions = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.sectionActions}>{children}</div>;
};

const RoleCard = ({ label, name, username }) => {
  const classes = useStyles();
  return (
    <div className={classes.card}>
      <div className={classes.roleLabel}>{label}</div>
      <div className={classes.roleValue}>
        {name ? (
          <>
            {name}
            {name === username && <span className={classes.roleYou}>Ty</span>}
          </>
        ) : (
          <span className={classes.roleEmpty}>nie przypisano</span>
        )}
      </div>
    </div>
  );
};

const ListShell = ({
  title = "Lista kontrolna",
  nrRef,
  rodzajOdprawy,
  status,
  username,
  osobaPrzygotowujaca,
  osobaWeryfikujaca,
  osobaWysylajaca,
  progress, // { done, total }
  backLabel,
  onBack,
  onLogout,
  actions,
  children,
}) => {
  const classes = useStyles();
  const percent =
    progress && progress.total > 0
      ? Math.round((progress.done / progress.total) * 100)
      : 0;

  return (
    <div className={classes.page}>
      <header className={classes.topBar}>
        <button type="button" className={classes.backBtn} onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
          {backLabel || "Powrót do tabeli"}
        </button>
        <span className={classes.divider} />
        <div className={classes.titleWrap}>
          <h1 className={classes.title}>{title}</h1>
          <span className={classes.nrRef}>{nrRef}</span>
          {rodzajOdprawy && (
            <span className={classes.kind}>{rodzajOdprawy}</span>
          )}
          <StatusPill status={status} />
        </div>
        <Tooltip title="Wyloguj">
          <IconButton onClick={onLogout} aria-label="Wyloguj">
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </header>

      <main className={classes.content}>
        <div className={classes.summary}>
          <RoleCard
            label="Osoba przygotowująca"
            name={osobaPrzygotowujaca}
            username={username}
          />
          <RoleCard
            label="Osoba weryfikująca"
            name={osobaWeryfikujaca}
            username={username}
          />
          <RoleCard
            label="Osoba wysyłająca"
            name={osobaWysylajaca}
            username={username}
          />
          {progress && (
            <div className={`${classes.card} ${classes.progressCard}`}>
              <div className={classes.progressHead}>
                <span className={classes.roleLabel}>Wypełnienie listy</span>
                <span className={classes.progressValue}>
                  {progress.done} / {progress.total}
                </span>
              </div>
              <LinearProgress
                variant="determinate"
                value={percent}
                color={percent === 100 ? "success" : "primary"}
                style={{ height: 8, borderRadius: 4 }}
              />
            </div>
          )}
        </div>

        {actions && <div className={classes.toolbar}>{actions}</div>}

        {children}
      </main>
    </div>
  );
};

export default ListShell;
